import { createI18n } from 'vue-i18n';
import axios from 'axios';
import { loadMessages, locale as dxSetLocale } from 'devextreme/localization';
import { setupResponseInterceptor, setupErrorInterceptor } from '@/interceptors/responseInterceptor';

const savedLocale = localStorage.getItem('locale');
const browserLocale = navigator.language.split('-')[0];
const availableLocales = ['en', 'tr', 'de'];
const locale = savedLocale || (availableLocales.includes(browserLocale) ? browserLocale : 'en');

const i18n = createI18n({
  legacy: false,
  locale,
  fallbackLocale: 'en',
  messages: {},
  globalInjection: true
});

const baseLocalePath = '/locales/';

const unflattenObject = (flatObj) => {
  const result = {};
  
  for (const key in flatObj) {
    const keys = key.split('.');
    let current = result;
    
    for (let i = 0; i < keys.length - 1; i++) {
      const k = keys[i];
      if (!(k in current)) {
        current[k] = {};
      }
      current = current[k];
    }
    
    current[keys[keys.length - 1]] = flatObj[key];
  }
  
  return result;
};

const deepMerge = (target, source) => {
  const result = { ...target };
  
  for (const key in source) {
    if (source[key] !== null && typeof source[key] === 'object' && !Array.isArray(source[key])) {
      result[key] = deepMerge(result[key] || {}, source[key]);
    } else {
      result[key] = source[key];
    }
  }
  
  return result;
};

export const changeLanguage = async (lang) => {
  try {
    const messages = await loadLanguageData(lang);
    loadMessages({ [lang]: messages });
    dxSetLocale(lang);

    i18n.global.setLocaleMessage(lang, messages);
    i18n.global.locale.value = lang;
    localStorage.setItem('locale', lang);
    
    clearTranslationCache();
    
    return true;
  } catch (err) {
    console.error('Error while changing language:', err);
    return false;
  }
};

export const initializeI18n = async () => {
  const currentLocale = i18n.global.locale.value;

  const messages = await loadLanguageData(currentLocale);
  i18n.global.setLocaleMessage(currentLocale, messages);

  loadMessages({ [currentLocale]: messages });
  dxSetLocale(currentLocale);

  setupAxiosInterceptors();

  return i18n;
};

function setupAxiosInterceptors() {
  // Response interceptor
  axios.interceptors.response.use(
    setupResponseInterceptor(),
    setupErrorInterceptor()
  );
}

// Cache yönetimi
const translationCache = new Map();
const CACHE_DURATION = 30 * 60 * 1000;

const createCacheEntry = (data) => ({
  data,
  timestamp: Date.now()
});

const isCacheValid = (cacheEntry) => {
  return cacheEntry && (Date.now() - cacheEntry.timestamp) < CACHE_DURATION;
};

export const loadLanguageData = async (lang) => {
  const cacheKey = `translations_${lang}`;
  const cachedEntry = translationCache.get(cacheKey);

  if (isCacheValid(cachedEntry)) {
    return cachedEntry.data;
  }

  try {
    const [defaultMessages, overrides] = await Promise.allSettled([
      loadDefaultMessages(lang),
      loadApiOverrides(lang)
    ]);

    const defaultData = defaultMessages.status === 'fulfilled' ? defaultMessages.value : {};
    const overrideData = overrides.status === 'fulfilled' ? overrides.value : {};

    const merged = deepMerge(defaultData, overrideData);

    translationCache.set(cacheKey, createCacheEntry(merged));
    return merged;

  } catch (err) {
    console.error('Critical error while loading language data:', err);

    if (cachedEntry) {
      console.warn('Using expired cache due to error');
      return cachedEntry.data;
    }

    return i18n.global.getLocaleMessage(lang) || {};
  }
};

const loadDefaultMessages = async (lang) => {
  try {
    const response = await fetch(`${baseLocalePath}${lang}.json`, {
      cache: 'force-cache'
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (err) {
    console.warn(`Could not load default language file: ${lang}`, err.message);
    return {};
  }
};

const loadApiOverrides = async (lang) => {
  try {
    const response = await fetch(`/api/translations/${lang}`, {
      headers: {
        'Accept': 'application/json',
        'Cache-Control': 'max-age=1800'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    
    if (!data || typeof data !== 'object') {
      return {};
    }

    const nestedData = unflattenObject(data);
    
    return nestedData;
    
  } catch (err) {
    console.warn('Failed to load API override data:', err.message);
    return {};
  }
};

export const clearTranslationCache = (lang = null) => {
  if (lang) {
    translationCache.delete(`translations_${lang}`);
  } else {
    translationCache.clear();
  }
  
  if (window.translationCache) {
    window.translationCache.clear();
  }
};

export const getCacheStats = () => ({
  size: translationCache.size,
  keys: Array.from(translationCache.keys()),
  totalMemory: JSON.stringify(Array.from(translationCache.values())).length
});

export default i18n;